import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { NAV_REGISTRY, NavItemDef, visibleForRole } from '@/lib/navRegistry'

const STORAGE_KEY = 'app_mobile_nav_pinned'
const MAX_PINNED = 4

function loadPinned(): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed.filter((k: unknown): k is string => typeof k === 'string')
  } catch {
    return null
  }
}

function savePinned(keys: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys))
  } catch {
    // ignore quota errors
  }
}

// Module-level pub/sub so every consumer of the hook stays in sync within
// the same tab. The browser's `storage` event only fires for OTHER tabs,
// so without this the bottom nav and the Settings panel each have their
// own copy of state and edits in one don't reach the other.
const listeners = new Set<(keys: string[]) => void>()

function broadcast(keys: string[]) {
  listeners.forEach(l => l(keys))
}

/**
 * Manages which nav items the user has pinned to the mobile bottom nav.
 * Always returns up to MAX_PINNED keys, all of which are visible to the
 * user's current role. Items that become invisible (role/feature changes)
 * are dropped silently.
 */
export function useMobileNavPrefs() {
  const { agent } = useAuth()
  const role = agent?.role as 'admin' | 'supervisor' | 'agent' | undefined
  const allowed = visibleForRole(role)

  const computeDefault = useCallback((): string[] => {
    return allowed.slice(0, MAX_PINNED).map(i => i.key)
  }, [role])

  const [pinnedKeys, setPinnedKeys] = useState<string[]>(() => {
    const stored = loadPinned()
    if (!stored) return computeDefault()
    const allowedKeys = new Set(allowed.map(i => i.key))
    const filtered = stored.filter(k => allowedKeys.has(k)).slice(0, MAX_PINNED)
    return filtered.length > 0 ? filtered : computeDefault()
  })

  // In-tab sync: subscribe to changes from other hook instances.
  useEffect(() => {
    listeners.add(setPinnedKeys)
    return () => {
      listeners.delete(setPinnedKeys)
    }
  }, [])

  // Cross-tab sync: re-read when localStorage changes in another tab.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      const next = loadPinned()
      if (next) {
        setPinnedKeys(next)
        broadcast(next)
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const persist = useCallback((keys: string[]) => {
    savePinned(keys)
    broadcast(keys)
  }, [])

  const setPinned = useCallback((keys: string[]) => {
    const allowedKeys = new Set(allowed.map(i => i.key))
    const cleaned = keys.filter(k => allowedKeys.has(k)).slice(0, MAX_PINNED)
    setPinnedKeys(cleaned)
    persist(cleaned)
  }, [role, persist])

  const togglePin = useCallback((key: string) => {
    setPinnedKeys(prev => {
      let next: string[]
      if (prev.includes(key)) {
        next = prev.filter(k => k !== key)
      } else {
        if (prev.length >= MAX_PINNED) return prev
        next = [...prev, key]
      }
      persist(next)
      return next
    })
  }, [persist])

  const resetToDefault = useCallback(() => {
    const def = computeDefault()
    setPinnedKeys(def)
    persist(def)
  }, [computeDefault, persist])

  const pinnedItems: NavItemDef[] = pinnedKeys
    .map(k => NAV_REGISTRY.find(i => i.key === k))
    .filter((i): i is NavItemDef => Boolean(i))

  // "More" sheet contents — everything visible that isn't already pinned
  const overflowItems: NavItemDef[] = allowed.filter(i => !pinnedKeys.includes(i.key))

  return {
    pinnedKeys,
    pinnedItems,
    overflowItems,
    availableItems: allowed,
    setPinned,
    togglePin,
    resetToDefault,
    maxPinned: MAX_PINNED,
  }
}
